import { useEffect, useMemo, useRef, useState } from 'react'

import {
  calcReportParamsSection,
  calcReportParamsSectionLegacy,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/vk/lib/report-params'
import useParamsReseed, { isUnchanged } from '@aytm/app/stats/page/question-views/components/hooks/useParamsReseed'

// The values the vk<key> section carries; a change to any of them is written back.
const PERSISTED = ['period', 'fqPeriod', 'infographic']

// The controls address the block by the names the Backbone model used.
const MODEL_KEYS = { fq_period: 'fqPeriod', navto: 'navTo' }

const normalize = (change = {}) =>
  Object.entries(change).reduce((acc, [key, value]) => {
    acc[MODEL_KEYS[key] || key] = value
    return acc
  }, {})

// Owns the Van Westendorp / Konan block state - the period pair, the infographic toggle, the
// pricing config and the transient view state (scroll anchor, loader) - seeded from the vk<key>
// section and re-seeded whenever it changes outside the block.
//
// The page view never parses the section itself: it receives an api on mount and pulls the
// payloads it needs (the section in whichever grammar the report uses, the export payload) when
// a change is announced through onStateChange. A config change is announced as its own reason,
// since the block has to refetch its data for it.
export default function useVanKonanState({
  params,
  paramsRevision,
  format,
  components,
  answer,
  isReportXpert,
  onReady,
  onStateChange
}) {
  const [state, setState] = useState(() => resolveReportParams({ section: params?.vk, format, components, answer }))

  const snapshot = useRef()
  snapshot.current = { state, section: params?.vk, format, components, answer, isReportXpert }

  // 'seed' | 'config' | 'state' | null - what the next state commit means to the page view
  const pendingReason = useRef('seed')

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady

  // The block builds itself when it is scrolled to, which on a slow report is before its answer
  // has arrived -- and the pricing config is derived from the answer, so seeding then produces an
  // empty one. It is seeded again the first time a real answer is in hand. Only that once: the
  // block refetches on every pricing edit, so re-seeding on any answer would throw the reader's
  // own edit away each time their change came back.
  const hasAnswer = null != answer && 'loading' !== answer
  const seededFromAnswer = useRef(hasAnswer)

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'config' a pricing change the block has to refetch for, 'state' anything else worth
  // persisting. Transient view state (scroll anchor, loader) is announced as nothing.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  // `silent` writes state without announcing it: the post-refetch config reset would otherwise
  // read as a fresh pricing edit and refetch again.
  const onModelChange = useMemo(
    () =>
      (rawChange = {}, options = {}) =>
        setState(current => {
          const change = normalize(rawChange)

          // a write that changes nothing must not produce a new state object: callers fire from
          // effects, and a fresh object there would re-render and re-fire them forever
          if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

          const next = { ...current, ...change }

          if (!options.silent) {
            if (Object.hasOwn(change, 'config')) pendingReason.current = 'config'
            else if (PERSISTED.some(key => next[key] !== current[key])) pendingReason.current = 'state'
          }

          return next
        }),
    []
  )

  const api = useMemo(
    () => ({
      getReportParamsSection: () => {
        const { state, section, format, answer, isReportXpert } = snapshot.current
        const calc = isReportXpert ? calcReportParamsSection : calcReportParamsSectionLegacy

        return calc({ state, section, format, answer })
      },
      getExportData: () => {
        const { period, fqPeriod, infographic, config } = snapshot.current.state

        return { period, fq_period: fqPeriod, infographic, config }
      },
      update: (change, options) => onModelChange(change, options)
    }),
    [onModelChange]
  )

  // Clearing the section is a state change like any other: the writer resolves the defaults
  // back to an empty section, and the block refetches for it.
  const onReset = useMemo(
    () => () =>
      setState(() => {
        const { format, components, answer } = snapshot.current
        pendingReason.current = 'reset'

        return resolveReportParams({ format, components, answer })
      }),
    []
  )

  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // The section moved outside the block (a report revert, another view of the question, the URL):
  // re-seed from it.
  useParamsReseed(params?.vk, paramsRevision, section => {
    const { format, components, answer } = snapshot.current
    const resolved = resolveReportParams({ section, format, components, answer })
    // the loader belongs to a round trip still out, which a re-read of the params does not end
    setState(current => {
      const next = { ...resolved, loading: current.loading }
      return isUnchanged(current, next) ? current : next
    })
  })

  useEffect(() => {
    if (seededFromAnswer.current || !hasAnswer) return
    seededFromAnswer.current = true

    const { section, format, components, answer } = snapshot.current
    setState(resolveReportParams({ section, format, components, answer }))
  }, [hasAnswer])

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  return { ...state, onModelChange, onReset }
}
